import { z } from 'zod'

export const CRM_REPORT_READ_CONTRACT_VERSION = '1.0.0' as const

const optionalQueryString = z.preprocess(
  (value) => (typeof value === 'string' ? value : undefined),
  z.string().optional(),
)

/** Query keys accepted by every CRM reporting read alias. Domain validation remains in the route. */
export const crmReportReadQuerySchema = z.object({
  orgId: optionalQueryString,
  org_id: optionalQueryString,
  ownerUserId: optionalQueryString,
  owner_user_id: optionalQueryString,
  actorUserId: optionalQueryString,
  page: optionalQueryString,
  offset: optionalQueryString,
  limit: optionalQueryString,
  date_from: optionalQueryString,
  date_to: optionalQueryString,
}).passthrough()

const reportingSideEffectsSchema = z.object({
  db1Read: z.boolean(),
  billingTouched: z.literal(false),
  eventEmitted: z.literal(false),
  uploadTouched: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.literal(false),
}).passthrough()

const reportingReadEvidenceSchema = z.object({
  db1TablesRead: z.array(z.enum(['crm_leads', 'crm_tasks', 'crm_activities'])),
  rawLeadNotesReturned: z.literal(false),
  billingTouched: z.literal(false),
  eventEmitted: z.literal(false),
  uploadTouched: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.literal(false),
}).passthrough()

const dashboardSummarySchema = z.object({
  totalLeads: z.number().finite(),
  openLeads: z.number().finite(),
  wonLeads: z.number().finite(),
  lostLeads: z.number().finite(),
  activeTasks: z.number().finite(),
  overdueTasks: z.number().finite(),
  activityCount: z.number().finite(),
}).passthrough()

const dashboardBreakdownSchema = z.object({
  stage: z.string().min(1),
  count: z.number().finite(),
}).passthrough()

const dashboardSourceBreakdownSchema = z.object({
  source: z.string().min(1),
  count: z.number().finite(),
}).passthrough()

const dashboardTrendSchema = z.object({
  date: z.string().min(1),
  count: z.number().finite(),
}).passthrough()

const dashboardAlertsSchema = z.object({
  unassigned: z.number().finite(),
  missingPhoneE164: z.number().finite(),
  missingCountry: z.number().finite(),
}).passthrough()

export const crmDashboardSummaryResponseSchema = z.object({
  contract_version: z.literal(CRM_REPORT_READ_CONTRACT_VERSION),
  summary: dashboardSummarySchema,
  leadsByStage: z.array(dashboardBreakdownSchema),
  leadsBySource: z.array(dashboardSourceBreakdownSchema),
  newLeadsTrend: z.array(dashboardTrendSchema),
  alerts: dashboardAlertsSchema,
  readEvidence: reportingReadEvidenceSchema,
  sideEffects: reportingSideEffectsSchema,
}).passthrough()

const pipelineStageSchema = z.object({
  stage: z.string().min(1),
  leadCount: z.number().finite(),
  openCount: z.number().finite(),
  wonCount: z.number().finite(),
  lostCount: z.number().finite(),
}).passthrough()

export const crmPipelineResponseSchema = z.object({
  contract_version: z.literal(CRM_REPORT_READ_CONTRACT_VERSION),
  stages: z.array(pipelineStageSchema),
  readEvidence: reportingReadEvidenceSchema,
  sideEffects: reportingSideEffectsSchema,
}).passthrough()

const funnelStageSchema = z.object({
  stage: z.string().min(1),
  count: z.number().finite(),
  amountCents: z.number().finite(),
  weightedAmountCents: z.number().finite(),
}).passthrough()

const funnelTotalsSchema = z.object({
  count: z.number().finite(),
  amountCents: z.number().finite(),
  weightedAmountCents: z.number().finite(),
}).passthrough()

const funnelSideEffectsSchema = z.object({
  db1Written: z.boolean(),
  auditWritten: z.boolean(),
  billingTouched: z.literal(false),
  eventEmitted: z.literal(false),
  uploadTouched: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.boolean(),
}).passthrough()

export const crmFunnelSummaryResponseSchema = z.object({
  contract_version: z.literal(CRM_REPORT_READ_CONTRACT_VERSION),
  funnel: z.object({
    stages: z.array(funnelStageSchema),
    totals: funnelTotalsSchema,
  }).passthrough(),
  sideEffects: funnelSideEffectsSchema,
}).passthrough()

const sourceFunnelRowSchema = z.object({
  source: z.string().min(1),
  leadCount: z.number().finite(),
  openCount: z.number().finite(),
  wonCount: z.number().finite(),
  lostCount: z.number().finite(),
}).passthrough()

export const crmSourceFunnelResponseSchema = z.object({
  contract_version: z.literal(CRM_REPORT_READ_CONTRACT_VERSION),
  sources: z.array(sourceFunnelRowSchema),
  readEvidence: reportingReadEvidenceSchema,
  sideEffects: reportingSideEffectsSchema,
}).passthrough()

const stageAgingRowSchema = z.object({
  stage: z.string().min(1),
  leadCount: z.number().finite(),
  oldestLeadAt: z.string().nullable(),
  newestLeadAt: z.string().nullable(),
}).passthrough()

export const crmStageAgingResponseSchema = z.object({
  contract_version: z.literal(CRM_REPORT_READ_CONTRACT_VERSION),
  stages: z.array(stageAgingRowSchema),
  readEvidence: reportingReadEvidenceSchema,
  sideEffects: reportingSideEffectsSchema,
}).passthrough()

export const CRM_REPORT_READ_ERROR_CODES = [
  'CR_VALIDATION_FAILED',
  'CR_PERMISSION_DENIED',
  'CR_AUTH_UNAVAILABLE',
  'CR_MEMBERSHIP_UNAVAILABLE',
  'CR_DOMAIN_UNAVAILABLE',
] as const

export const crmReportErrorEnvelopeSchema = z.object({
  contract_version: z.literal(CRM_REPORT_READ_CONTRACT_VERSION),
  error: z.object({
    code: z.enum(CRM_REPORT_READ_ERROR_CODES),
    message: z.string().min(1),
    reference: z.string().min(1),
    details: z.record(z.unknown()).optional(),
    request_id: z.string().optional(),
  }).passthrough(),
}).passthrough()

export type CrmReportReadQuery = z.infer<typeof crmReportReadQuerySchema>
export type CrmDashboardSummaryResponse = z.infer<typeof crmDashboardSummaryResponseSchema>
export type CrmPipelineResponse = z.infer<typeof crmPipelineResponseSchema>
export type CrmFunnelSummaryResponse = z.infer<typeof crmFunnelSummaryResponseSchema>
export type CrmSourceFunnelResponse = z.infer<typeof crmSourceFunnelResponseSchema>
export type CrmStageAgingResponse = z.infer<typeof crmStageAgingResponseSchema>
export type CrmReportErrorCode = (typeof CRM_REPORT_READ_ERROR_CODES)[number]
export type CrmReportErrorEnvelope = z.infer<typeof crmReportErrorEnvelopeSchema>
